import {
  AutocompleteInput,
  DateInput,
  Edit,
  NumberInput,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  TextInput,
} from "@/components/admin";
import { FileText, Landmark, Receipt } from "lucide-react";
import { required, useRecordContext } from "ra-core";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { DocumentItemsTable } from "../shared/DocumentItemsTable";
import { QuotationMarkupField } from "./QuotationMarkup";
import { GrandTotalsWatcher } from "../shared/GrandTotalsWatcher";
import { RevisedReferenceWatcher } from "../shared/RevisedReferenceWatcher";
import { TermsTemplateSelector } from "../shared/TermsTemplateSelector";
import { WatermarkOverlay } from "../shared/WatermarkOverlay";
import { requireNonZeroTotal } from "../shared/validators";
import { FilteredContactInput } from "../../misc/FilteredContactInput";

const QuotationEditTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <div className="flex items-center gap-3">
      <Button
        asChild
        variant="outline"
        size="sm"
        className="gap-1.5 bg-[#00a68d]/10 hover:bg-[#00a68d]/20 text-[#00a68d] hover:text-[#00947e] border border-[#00a68d]/20 hover:border-[#00a68d]/30 font-semibold cursor-pointer h-9 px-4 rounded-lg"
      >
        <Link to="/quotations" className="flex items-center gap-1.5">
          <ArrowLeft className="w-4 h-4 text-[#00a68d]" /> Back
        </Link>
      </Button>
      <span className="text-xl font-bold tracking-tight">
        Edit Quotation {record.reference}
      </span>
    </div>
  );
};

export const QuotationEdit = () => (
  <Edit title={<QuotationEditTitle />} mutationMode="pessimistic">
    <SimpleForm className="max-w-5xl mx-auto space-y-12 relative bg-transparent px-2 pb-16" validate={requireNonZeroTotal}>
      <WatermarkOverlay />

      {/* Section 1: Quotation Details */}
      <div className="w-full space-y-8 bg-transparent">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
          {/* Quotation No. */}
          <div className="grid grid-cols-12 gap-3 items-center w-full">
            <label className="col-span-12 md:col-span-4 text-sm font-semibold text-slate-600 dark:text-slate-300 md:text-right pr-2">
              Quotation No.
            </label>
            <div className="col-span-12 md:col-span-8">
              <TextInput
                source="reference"
                label={false}
                disabled
                className="w-full opacity-70"
              />
            </div>
          </div>

          {/* Status */}
          <div className="grid grid-cols-12 gap-3 items-center w-full">
            <label className="col-span-12 md:col-span-4 text-sm font-semibold text-slate-600 dark:text-slate-300 md:text-right pr-2">
              Status
            </label>
            <div className="col-span-12 md:col-span-8">
              <SelectInput
                source="status"
                label={false}
                className="w-full"
                choices={[
                  { id: "draft", name: "Draft" },
                  { id: "sent", name: "Sent" },
                  { id: "approved", name: "Approved" },
                  { id: "rejected", name: "Rejected" },
                  { id: "revised", name: "Revised" },
                ]}
              />
            </div>
          </div>

          {/* Date */}
          <div className="grid grid-cols-12 gap-3 items-center w-full">
            <label className="col-span-12 md:col-span-4 text-sm font-semibold text-slate-600 dark:text-slate-300 md:text-right pr-2">
              Date *
            </label>
            <div className="col-span-12 md:col-span-8">
              <DateInput
                source="date"
                label={false}
                validate={required()}
                className="w-full"
              />
            </div>
          </div>

          {/* Valid Until */}
          <div className="grid grid-cols-12 gap-3 items-center w-full">
            <label className="col-span-12 md:col-span-4 text-sm font-semibold text-slate-600 dark:text-slate-300 md:text-right pr-2">
              Valid Until
            </label>
            <div className="col-span-12 md:col-span-8">
              <DateInput
                source="valid_until"
                label={false}
                className="w-full"
              />
            </div>
          </div>

          {/* Company */}
          <div className="grid grid-cols-12 gap-3 items-center w-full">
            <label className="col-span-12 md:col-span-4 text-sm font-semibold text-slate-600 dark:text-slate-300 md:text-right pr-2">
              Company *
            </label>
            <div className="col-span-12 md:col-span-8">
              <ReferenceInput source="company_id" reference="companies">
                <AutocompleteInput
                  label={false}
                  validate={required()}
                  className="w-full"
                />
              </ReferenceInput>
            </div>
          </div>

          {/* Customer Contact */}
          <div className="grid grid-cols-12 gap-3 items-center w-full">
            <label className="col-span-12 md:col-span-4 text-sm font-semibold text-slate-600 dark:text-slate-300 md:text-right pr-2">
              Customer Contact
            </label>
            <div className="col-span-12 md:col-span-8">
              <FilteredContactInput source="customer_id" label={false} className="w-full" />
            </div>
          </div>

          {/* Customer Name Override - spans across both columns */}
          <div className="grid grid-cols-12 gap-3 items-center w-full md:col-span-2">
            <label className="col-span-12 md:col-span-2 text-sm font-semibold text-slate-600 dark:text-slate-300 md:text-right pr-2">
              Name Override *
            </label>
            <div className="col-span-12 md:col-span-10">
              <TextInput
                source="customer_name"
                label={false}
                validate={required()}
                helperText="Required for record - usually auto-filled from contact/company"
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Billing & Shipping Addresses */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 pt-6 border-t border-slate-100">
          <div className="grid grid-cols-12 gap-3 items-start w-full">
            <label className="col-span-12 md:col-span-4 text-sm font-semibold text-slate-600 dark:text-slate-300 md:text-right pr-2 pt-2">
              Billing Address
            </label>
            <div className="col-span-12 md:col-span-8">
              <TextInput
                source="billing_address"
                label={false}
                multiline
                rows={3}
                helperText="Auto-filled from company; edit to override"
                className="w-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-12 gap-3 items-start w-full">
            <label className="col-span-12 md:col-span-4 text-sm font-semibold text-slate-600 dark:text-slate-300 md:text-right pr-2 pt-2">
              Shipping Address
            </label>
            <div className="col-span-12 md:col-span-8">
              <TextInput
                source="shipping_address"
                label={false}
                multiline
                rows={3}
                helperText="Auto-filled from company; edit to override"
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: Line Items & Financials */}
      <div className="w-full space-y-6 bg-transparent">
        <div className="flex items-center gap-2.5 pb-3 border-b border-[#00a68d]/20">
          <Landmark className="w-5 h-5 text-[#00a68d]" />
          <h3 className="text-lg font-bold tracking-tight text-slate-800 dark:text-slate-200">
            Line Items & Financials
          </h3>
        </div>
        <div className="space-y-6">
          <QuotationMarkupField />
          <DocumentItemsTable showCost showSubItems />
          <GrandTotalsWatcher />
          <RevisedReferenceWatcher />

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 py-6 border-t border-slate-100">
            <NumberInput
              source="cgst_amount"
              label="CGST (₹)"
              readOnly
              className="w-full"
            />
            <NumberInput
              source="sgst_amount"
              label="SGST (₹)"
              readOnly
              className="w-full"
            />
            <NumberInput
              source="igst_amount"
              label="IGST (₹)"
              helperText="Enter manually for interstate"
              className="w-full"
            />
            <NumberInput
              source="total_amount"
              label="Grand Total (₹)"
              readOnly
              className="w-full font-bold text-[#00a68d]"
            />
            <div className="md:col-span-4">
              <TextInput
                source="amount_in_words"
                label="Amount in Words"
                className="w-full italic"
                readOnly
              />
            </div>
          </div>
        </div>
      </div>

      {/* Section 3: Terms & Notes */}
      <div className="w-full space-y-6 bg-transparent">
        <div className="flex items-center justify-between pb-3 border-b border-[#00a68d]/20">
          <div className="flex items-center gap-2.5">
            <FileText className="w-5 h-5 text-[#00a68d]" />
            <h3 className="text-lg font-bold tracking-tight text-slate-800 dark:text-slate-200">
              Terms & Notes
            </h3>
          </div>
          <TermsTemplateSelector type="quotation" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <TextInput
            source="terms_conditions"
            label="Terms & Conditions"
            multiline
            rows={4}
            className="w-full max-w-none"
          />
          <TextInput
            source="notes"
            label="Internal Notes"
            multiline
            rows={2}
            className="w-full max-w-none"
          />
        </div>
      </div>
    </SimpleForm>
  </Edit>
);
